import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { withRouterProps } from '../../utils/with-router-props'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../../constants/routes'
import { fetchApiDataIfNeeded } from '../../../actions/api'
import { PaginationWrapper } from 'shs-pagination'
import { createSelector } from '@reduxjs/toolkit'
import moment from 'moment'
import AccountSidebar from '../../shared/account-sidebar'

const selectInstallations = (state, accountId) => state.adminInstallations.filter(install => install.accountId === Number(accountId))
const selectInstallPager = state => state.paginator.adminInstallations

const makeInstallations = createSelector(
    [selectInstallations, selectInstallPager],
    (installations, paginator) => {
        if (paginator) {
            return installations
                .filter(field => paginator.itemIds.indexOf(field.id) !== -1)
                .sort((a, b) => {
                    if (paginator.itemIds.indexOf(a.id) > paginator.itemIds.indexOf(b.id)) {
                        return 1
                    }
                    return -1
                })
        }
        return []
    }
)

const mapStateToProps = (state, ownProps) => {
    return {
        installations: makeInstallations(state, ownProps.params.accountId),
        paginator: selectInstallPager(state),
        account: state.adminAccounts.find(acc => acc.id === Number(ownProps.params.accountId))
    }
}

const AdminAccountDashboard = ({ dispatch, installations, account, params, paginator }) => {
    useEffect(() => {
        if (params.accountId) {
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/installations', 'LIST', null), { filter: [`account_id|${params.accountId}`] }))
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/accounts', 'VIEW', params.accountId)))
        }
    }, [dispatch, params.accountId])

    const handlePaginationUpdate = (paginationParams) => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/installations', 'LIST', null), { filter: [`account_id|${params.accountId}`] }, paginationParams))
    }

    const renderInstallations = () => {
        if (!paginator) {
            return null
        }
        if (!installations || installations.length === 0) {
            return (
                <div className="no-data">
                    <p>No installations found.</p>
                </div>
            )
        }
        let allInstalls = []
        installations.forEach(install => {
            allInstalls.push(
                <tr
                    key={install.id}
                    className={install._computed?.statusDisplay === 'Needs Attention' ? 'error' : ''}>
                    <td className="title">
                        VIN: {install.vin}
                    </td>
                    <td className="main">
                        {install._computed.accountName}
                    </td>
                    <td className="main">
                        Last Update: {install.updatedAt ? moment(install.updatedAt).format('MMM Do \'YY h:mm a') : 'N/A'}
                    </td>
                    <td className="status">
                        {install._computed.statusDisplay}
                    </td>
                    <td className="main">
                        <Link
                            className="btn outline small"
                            to={buildRoutePath(routes.adminInstallationView.path, { installId: install.id })}>
                                View
                        </Link>
                    </td>
                </tr>
            )
        })

        return (
            <PaginationWrapper
                paginator={paginator}
                controls={{ search: { enabled: true, collapsed: false } }}
                countOptions={[5, 10, 25, 50, 100]}
                callbackFX={handlePaginationUpdate}>
                <table className="table">
                    <thead>
                        <tr>
                            <th>VIN</th>
                            <th>Account</th>
                            <th className="mobile-hide">Updated</th>
                            <th>Status</th>
                            <th>&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allInstalls}
                    </tbody>
                </table>
            </PaginationWrapper>
        )
    }

    if (!account) {
        return null
    }

    return (
        <div className="admin-dashboard">
            <div className="header">
                <div className="container">
                    <div className="header-details">
                        <h1>Dashboard for {account.name}</h1>
                        <p>View and manage installations for this account.</p>
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="sidebar-layout">
                    <div className="sidebar box">
                        <AccountSidebar account={account} />
                    </div>
                    <div className="main-content box">
                        <div className="container">
                            {renderInstallations()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default withRouterProps(connect(mapStateToProps)(AdminAccountDashboard))
